<template>
  <v-card>
    <v-card-title>
      Assets Count Management
      <v-spacer></v-spacer>
      <v-btn
        color="white"
        dark
        @click="showCloseDialog"
        class="mr-2 tw-text-black"
        :disabled="selectedList.length === 0"
      >
        <v-icon left>mdi-check-circle</v-icon>
        Close ({{ selectedList.length }})
      </v-btn>
      <v-btn
      v-if="role.owner || isDevOrEngineer"
        color="primary"
        dark
        @click="showEmailDialog"
        class="mr-2"
        :disabled="selectedList.length === 0"
      >
        <v-icon left>mdi-email</v-icon>
        Send Email ({{ selectedList.length }})
      </v-btn>
      <v-btn 
        v-if="role.owner || isDevOrEngineer"
        color="success" 
        dark 
        @click="showCreateDialog" 
        class="mr-2"
      >
        <v-icon left>mdi-plus</v-icon>
        Create Assets Count
      </v-btn>
    </v-card-title>

    <!-- <v-card-actions class="py-2">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search Session"
        dense
        hide-details
        class="mx-2"
        style="max-width: 300px"
      /> 
        <v-icon left>mdi-file-excel</v-icon>
        Export CSV
      </v-btn>
    </v-card-actions> -->

    <!-- Filters Section -->
    <v-card-text>
      <v-row align="center">
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.department"
            :items="departments"
            item-text="name"
            item-value="id"
            label="Department"
            clearable
            dense
            outlined
            @change="applyFilters"
          >
            <template v-slot:prepend-item>
              <v-list-item @click="filters.department = null">
                <v-list-item-content>
                  <v-list-item-title>All Departments</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-select>
        </v-col>
        
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.location"
            :items="locations"
            item-text="name"
            item-value="id"
            label="Location"
            clearable
            dense
            outlined
            @change="applyFilters"
          >
            <template v-slot:prepend-item>
              <v-list-item @click="filters.location = null">
                <v-list-item-content>
                  <v-list-item-title>All Locations</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-select>
        </v-col>
        
        <v-col cols="12" md="3" >
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            :return-value.sync="filters.date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.date"
                label="Count Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                outlined
                clearable
                @click:clear="clearDate"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="filters.date"
              no-title
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="dateMenu = false"
              >
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dateMenu.save(filters.date); applyFilters()"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" md="3" class="d-flex align-center">
          <v-btn
            color="primary"
            @click="clearAllFilters"
            outlined
            small
            :disabled="isLoading"
            style="transform: translateY(-10px);"
            :loading="isLoading"
          >
            <v-icon left small>mdi-filter-off</v-icon>
            Clear Filters
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <div class="table-container" style="position: relative;">
      <v-overlay :value="isLoading" absolute color="white" opacity="0.8">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-3 text-center">Loading...</div>
      </v-overlay>

      <v-simple-table>
        <thead>
          <tr>
            <th>
              <v-checkbox
                :input-value="selectedAll"
                @change="clickSelectedAll($event)"
                color="#6ec39b"
                class="ma-0 pa-0"
                hide-details
              />
            </th>
            <th
            class="order-by-row"
            @click="changeOrderBy('taskNumber')"
            >FAC No.
            <v-icon v-if="orderBySelect.key === 'taskNumber'" small>
              {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
            </v-icon>
            </th>
            <th class="order-by-row" @click="changeOrderBy('departmentName')">
              Department
              <v-icon v-if="orderBySelect.key === 'departmentName'" small>
                {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th class="order-by-row" @click="changeOrderBy('locationName')">
              Location
              <v-icon v-if="orderBySelect.key === 'locationName'" small>
                {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th class="order-by-row" @click="changeOrderBy('createdAt')">
              Count Date
              <v-icon v-if="orderBySelect.key === 'createdAt'" small>
                {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th class="order-by-row" @click="changeOrderBy('assetsTotal')">
              Total Assets
              <v-icon v-if="orderBySelect.key === 'assetsTotal'" small>
                {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th class="order-by-row" @click="changeOrderBy('assetsCount')">
              Counted
              <v-icon v-if="orderBySelect.key === 'assetsCount'" small>
                {{ orderBySelect.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th>Progress</th>
            <th>Status</th>
            <th>Count By</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredData.length && !isLoading">
            <td :colspan="10" class="text-center py-8">ไม่พบเอกสารที่ต้องการ</td>
          </tr>
          <tr
            v-for="(item) in filteredData"
            :key="item.id"
            class="clickable-row"
          >
            <td @click.stop>
              <v-checkbox
                :input-value="isChecked(item.id)"
                @change="toggleSelect(item)"
                :disabled="isItemDisabled(item)"
                color="#6ec39b"
                class="ma-0 pa-0"
                hide-details
              />
            </td>
            <td @click="navigateToAssetsListPage(item)">{{ item.taskNumber }}</td>
            <td @click="navigateToAssetsListPage(item)">{{ item.departmentName }}</td>
            <td @click="navigateToAssetsListPage(item)">{{ item.locationName }}</td>
            <td @click="navigateToAssetsListPage(item)">{{ formatDate(item.createdAt) }}</td>
            <td @click="navigateToAssetsListPage(item)">
              <v-chip small color="primary">{{ item.assetsTotal }}</v-chip>
            </td>
            <td @click="navigateToAssetsListPage(item)">
              <v-chip
                small
                :color="item.assetsCount === item.assetsTotal ? 'success' : 'warning'"
              >
                {{ item.assetsCount }}
              </v-chip>
            </td>
            <td @click="navigateToAssetsListPage(item)">
              <v-progress-linear
                :value="(item.assetsCount / item.assetsTotal) * 100"
                height="20"
                :color="item.assetsCount === item.assetsTotal ? 'success' : 'warning'"
              >
                <template v-slot:default="{ value }">
                  <small class="tw-text-white">{{ Math.round(value) === 100 ? '100' : value.toFixed(1) }}%</small>
                </template>
              </v-progress-linear>
            </td>
            <td @click="navigateToAssetsListPage(item)">
              <v-chip
                :color="getCountStatusColor(item.status)"
                small
              >
                {{ item.status }}
              </v-chip>
            </td>
            <td @click="navigateToAssetsListPage(item)">{{ item.countBy }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- Session Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          Session Count Details
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text v-if="selectedSession">
          <v-row>
            <v-col cols="6">
              <strong>Department:</strong> {{ getDepartmentName(selectedSession.departmentId) }}
            </v-col>
            <v-col cols="6">
              <strong>Location:</strong> {{ getLocationName(selectedSession.locationId) }}
            </v-col>
            <v-col cols="6">
              <strong>Count Date:</strong> {{ formatDate(selectedSession.countDate) }}
            </v-col>
            <v-col cols="6">
              <strong>Created By:</strong> {{ selectedSession.createdBy }}
            </v-col>
          </v-row>
          
          <v-divider class="my-4"></v-divider>
          
          <h3>Asset Count Summary</h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Assets</td>
                  <td>{{ selectedSession.totalAssets }}</td>
                  <td>100%</td>
                </tr>
                <tr>
                  <td>Counted Assets</td>
                  <td>{{ selectedSession.countedAssets }}</td>
                  <td>{{ Math.round((selectedSession.countedAssets / selectedSession.totalAssets) * 100) }}%</td>
                </tr>
                <tr>
                  <td>Missing Assets</td>
                  <td>{{ selectedSession.totalAssets - selectedSession.countedAssets }}</td>
                  <td>{{ Math.round(((selectedSession.totalAssets - selectedSession.countedAssets) / selectedSession.totalAssets) * 100) }}%</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Create Session Count Dialog -->
    <v-dialog v-model="createDialog" max-width="900px">
      <v-card>
        <v-card-title>
          Create New Fixed Asset Count
          <v-spacer></v-spacer>
          <v-btn icon @click="createDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="createForm" v-model="validForm">
            <v-row>

              <v-col cols="12" md="6">
                <v-select
                  v-model="newSession.locationId"
                  :items="locations"
                  item-text="name"
                  item-value="id"
                  label="Location *"
                  :rules="[v => !!v || 'Location is required']"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-menu
                  ref="createDateMenu"
                  v-model="createDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="newSession.countDate"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="newSession.countDate"
                      label="Count Date *"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      :rules="[v => !!v || 'Count date is required']"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                    <v-date-picker
                    v-model="newSession.countDate"
                    no-title
                    scrollable
                    >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="createDateMenu = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="newSession.countDate = new Date().toISOString().substr(0, 10); $refs.createDateMenu.save(newSession.countDate)"
                    >
                      Now
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.createDateMenu.save(newSession.countDate)"
                    >
                      OK
                    </v-btn>
                    </v-date-picker>
                </v-menu>
              </v-col>
              

              <v-col cols="12" md="6">
                <v-select
                  v-model="newSession.departmentId"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Department *"
                  :rules="[v => !!v || 'Department is required']"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>

             
              
             
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newSession.description"
                  label="Description (Optional)"
                  outlined
                  dense
                  placeholder="Optional description"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="createDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :disabled="!validForm || creatingSession"
            :loading="creatingSession"
            @click="createSessionCount"
          >
            Create Task
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Email Dialog -->
    <v-dialog v-model="emailDialog" max-width="800px" scrollable>
      <v-card>
        <v-card-title>
          Send Selected Assets Count to Email
          <v-spacer></v-spacer>
          <v-btn icon @click="emailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 600px;">
          <v-alert type="info" dense text class="mb-4">
            <strong>{{ selectedList.length }}</strong> item(s) selected - Enter email for each item
          </v-alert>

          <v-form ref="emailForm" v-model="validEmailForm">
            <!-- Global Message -->
            <v-textarea
              v-model="globalMessage"
              label="Message for all emails (Optional)"
              prepend-icon="mdi-message-text"
              outlined
              dense
              rows="2"
              placeholder="This message will be included in all emails"
              class="mb-4"
            ></v-textarea>

            <v-divider class="mb-4"></v-divider>

            <!-- Email input for each selected item -->
            <div v-for="(item, index) in selectedList" :key="item.id" class="mb-4">
              <v-card outlined>
                <v-card-subtitle class="pb-2">
                  <strong>Item {{ index + 1 }}:</strong> {{ item.locationName }} - {{ formatDate(item.createdAt) }}
                  <br>
                  <span class="text-caption">
                    Counted: {{ item.assetsCount }}/{{ item.assetsTotal }} ({{ (() => { const p = (item.assetsCount / item.assetsTotal) * 100; return Math.round(p) === 100 ? '100' : p.toFixed(1); })() }}%)
                  </span>
                </v-card-subtitle>
                <v-card-text class="pt-0">
                  <v-autocomplete
                    v-model="emailPerItem[item.id]"
                    :items="companyUsers"
                    label="Email Address *"
                    prepend-icon="mdi-email"
                    :rules="emailRules"
                    outlined
                    dense
                    required
                    placeholder="Select or type email address"
                    clearable
                  ></v-autocomplete>

                  <v-checkbox
                    v-model="includeDetailsPerItem[item.id]"
                    label="Include Asset Count Details (Excel attachment)"
                    dense
                    hide-details
                    class="mt-2"
                  ></v-checkbox>
                </v-card-text>
              </v-card>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="emailDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!isAllEmailsValid() || sendingEmail"
            :loading="sendingEmail"
            @click="sendEmails"
          >
            <v-icon left>mdi-send</v-icon>
            Send {{ selectedList.length }} Email(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Count Confirmation Dialog -->
    <v-dialog v-model="closeDialog" max-width="700px" scrollable>
      <v-card>
        <v-card-title>
          <v-icon color="warning" left>mdi-alert-circle</v-icon>
          Confirm Close Assets Count
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 600px;">
          <v-alert type="warning" dense text class="mb-4">
            This action will:
            <ul class="mt-2">
              <li>Set 'InProgress' to 'Complete' status</li>
              <!-- <li>Mark the count session as complete</li>
              <li>Set progress to 100%</li> -->
            </ul>
          </v-alert>

          <v-alert type="info" dense text class="mb-4">
            <strong>{{ selectedList.length }}</strong> item(s) selected -
            <strong class="success--text">{{ selectedIncompleteItems.length }}</strong> can be closed
            <span v-if="selectedCompleteItems.length > 0">
              , <strong class="grey--text">{{ selectedCompleteItems.length }}</strong> already complete
            </span>
          </v-alert>

          <!-- Items that can be closed -->
          <div v-if="selectedIncompleteItems.length > 0">
            <h4 class="mb-2">Items to be closed ({{ selectedIncompleteItems.length }}):</h4>
            <div v-for="(item, index) in selectedIncompleteItems" :key="item.id" class="mb-3">
              <v-card outlined>
                <v-card-subtitle class="pb-2">
                  <strong>{{ index + 1 }}.</strong> {{ item.locationName }} - {{ formatDate(item.createdAt) }}
                  <br>
                  <span class="text-caption">
                    Current Progress: {{ item.assetsCount }}/{{ item.assetsTotal }}
                    ({{ (() => { const p = (item.assetsCount / item.assetsTotal) * 100; return Math.round(p) === 100 ? '100' : p.toFixed(1); })() }}%)
                  </span>
                  <br>
                  <v-chip x-small :color="getCountStatusColor(item.status)" class="mt-1">
                    {{ item.status }}
                  </v-chip>
                </v-card-subtitle>
              </v-card>
            </div>
          </div>

          <!-- Items already complete (will be skipped) -->
          <div v-if="selectedCompleteItems.length > 0" class="mt-4">
            <h4 class="mb-2 grey--text">Already complete - will be skipped ({{ selectedCompleteItems.length }}):</h4>
            <div v-for="(item, index) in selectedCompleteItems" :key="item.id" class="mb-3">
              <v-card outlined>
                <v-card-subtitle class="pb-2 grey--text">
                  <strong>{{ index + 1 }}.</strong> {{ item.locationName }} - {{ formatDate(item.createdAt) }}
                  <br>
                  <span class="text-caption">
                    Progress: {{ item.assetsCount }}/{{ item.assetsTotal }} (100%)
                  </span>
                  <br>
                  <v-chip x-small color="success" class="mt-1">
                    {{ item.status }}
                  </v-chip>
                </v-card-subtitle>
              </v-card>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <p class="mb-0">Are you sure you want to close {{ selectedIncompleteItems.length }} assets count session(s)?</p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="closingCount"
            :disabled="selectedIncompleteItems.length === 0"
            @click="confirmCloseCount"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Close {{ selectedIncompleteItems.length }} Item(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.msg }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import getRole from "@/libs/getRole";

export default {
  name: 'SessionCountTable',
  data() {
    return {
      loading: false,
      dateMenu: false,
      detailsDialog: false,
      selectedSession: null,
      createDialog: false,
      createDateMenu: false,
      validForm: false,
      creatingSession: false,
      search: '',
      selectedAll: false,
      selectedList: [],
      newSession: {
        departmentId: null,
        locationId: null,
        countDate: null,
        description: ''
      },
      filters: {
        department: null,
        location: null,
        date: null
      },
      fixedAssetsCounts: [],
      emailDialog: false,
      validEmailForm: false,
      sendingEmail: false,
      emailPerItem: {},
      includeDetailsPerItem: {},
      globalMessage: '',
      companyUsers: [], // Store company users emails
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      snackbar: {
        show: false,
        msg: '',
        color: 'success',
        icon: 'mdi-check-circle-outline'
      },
      closeDialog: false,
      closingCount: false
    }
  },
    computed: {
    ...mapState({
      assetCounts: state => state.fixedAssets.assetCounts,
      departments: state => state.fixedAssets.departments,
      locations: state => state.fixedAssets.locations,
      pagination: state => state.fixedAssets.assetsCountPagination,
      isLoading: state => state.fixedAssets.isLoading,
      user: (state) => state.user.userData,
      userCompany: (state) => state.user.userCompany,
    }),
    
    // Role checking
    role() {
      const userCompany = this.userCompany || {};
      return {
        owner: this.checkRole(userCompany) === "owner" && !userCompany?.isDeveloper,
        developer: this.checkRole(userCompany) === "developer",
        engineer: this.user?.isEngineer,
        admin: userCompany?.isAdmin,
        user: this.checkRole(userCompany) === "user"
      };
    },
    
    // Developer or Engineer
    isDevOrEngineer() {
      return this.role.developer || this.role.engineer;
    },
    
    filteredData() {
      // With Firebase pagination, we just apply client-side filters to the already paginated data
      // Note: For true server-side filtering, we should pass these filters to the Vuex action
      let data = [...this.assetCounts]

      // Apply search filter (client-side, for now)
      if (this.search) {
        const s = this.search.toLowerCase()
        data = data.filter(item =>
          (item.departmentName || '').toLowerCase().includes(s) ||
          (item.locationName || '').toLowerCase().includes(s) ||
          (item.countDate || '').toLowerCase().includes(s) ||
          (item.description || '').toLowerCase().includes(s)
        )
      }

      return data
    },
    
    orderBySelect() {
      return this.$store.state.fixedAssets?.assetsCountOrderBy || { key: 'createdAt', value: 'desc' };
    },

    selectedIncompleteItems() {
      return this.selectedList.filter(item => {
        const status = (item.status || '').toLowerCase()
        return status !== 'complete' && status !== 'completed'
      })
    },

    selectedCompleteItems() {
      return this.selectedList.filter(item => {
        const status = (item.status || '').toLowerCase()
        return status === 'complete' || status === 'completed'
      })
    }
  },

  watch: {
    selectedList() {
      // Check if all non-disabled items are selected
      const selectableItems = this.filteredData.filter(item => !this.isItemDisabled(item))
      this.selectedAll = this.selectedList.length === selectableItems.length && selectableItems.length > 0
    },
    
    // Watch for user data to load
    user: {
      handler(newUser, oldUser) {
        // If user just loaded and we haven't fetched data with user context yet
        if (newUser && newUser.email && (!oldUser || !oldUser.email)) {
          const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
          if (companyId) {
            const pageLimit = localStorage.getItem("pageLimit")
            const initialItemsPerPage = pageLimit === "auto" ? 10 : parseInt(pageLimit) || 10
            
            this.fetchAssetsCount({ 
              companyId, 
              itemsPerPage: initialItemsPerPage,
              userEmail: this.user?.email || null,
              isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
            })
          }
        }
      },
      immediate: false
    }
  },
    methods:{
    ...mapActions({
      fetchAssetsCount: 'fixedAssets/fetchAssetCount',
      fetchDepartments: 'fixedAssets/fetchDepartments',
      fetchLocations: 'fixedAssets/fetchLocations'
    }),
    
    // Role checking method
    checkRole(userCompany) {
      return getRole(userCompany);
    },
    
    async fetchFixedAssetsCounts() {
      this.loading = true
      try {
        const companyId = this.$route.params.companyId || localStorage.getItem('companyId')

        // Mock data - replace with actual Firebase query
         await this.$store.dispatch('fixedAssets/fetchAssetCount', { 
           companyId,
           userEmail: this.user?.email || null,
           isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
         })
      } catch (error) {
        console.error('Error fetching session counts:', error)
      } finally {
        this.loading = false
      }
    },
    
    applyFilters() {
      // Filters should trigger a new Firebase query
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId')

      // Reset to page 1 when filters change
      this.$store.commit('fixedAssets/resetAssetsCountPagination')

      // Fetch data with filters
      this.$store.dispatch('fixedAssets/fetchAssetCount', {
        companyId,
        departmentId: this.filters.department,
        locationId: this.filters.location,
        date: this.filters.date,
        page: 1,
        userEmail: this.user?.email || null,
        isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
      })
    },
    
    clearDate() {
      this.filters.date = null
      this.applyFilters()
    },

    clearAllFilters() {
      this.filters.department = null
      this.filters.location = null
      this.filters.date = null
      this.applyFilters()
    }, 

    isChecked(sessionId) {
      return this.selectedList.find(x => x.id === sessionId)
    },

    isItemDisabled(item) {
      const status = (item.status || '').toLowerCase()
      return status === 'complete' || status === 'completed'
    },

    toggleSelect(item) {
      const idx = this.selectedList.findIndex(x => x.id === item.id)
      if (idx === -1) this.selectedList.push(item)
      else this.selectedList.splice(idx, 1)
    },

    clickSelectedAll(checked) {
      if (checked) {
        // Only select items that are not disabled (not Complete)
        this.selectedList = this.filteredData.filter(item => !this.isItemDisabled(item))
      } else {
        this.selectedList = []
      }
    },

    changeOrderBy(order) {
      let orderBy = { ...this.orderBySelect }
      if (orderBy.key !== order) {
        orderBy = { key: order, value: 'asc' }
      } else {
        if (orderBy.value === 'asc') {
          orderBy.value = 'desc'
        } else {
          orderBy = { key: 'createdAt', value: 'desc' }
        }
      }
      // Update store with proper format
      this.$store.dispatch('fixedAssets/setAssetsCountOrderBy', {
        orderBy: orderBy,
      })

      // Reset to page 1 when sorting changes
      this.$store.commit('fixedAssets/resetAssetsCountPagination')

      // Refetch data with new ordering and current filters
      this.$store.dispatch('fixedAssets/fetchAssetCount', {
        companyId: this.$route.params.companyId || localStorage.getItem('companyId'),
        departmentId: this.filters.department,
        locationId: this.filters.location,
        date: this.filters.date,
        page: 1,
        userEmail: this.user?.email || null,
        isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
      })
    },    
    formatDate(timestamp) {
      if (!timestamp) return '' 
      // Handle Firebase Timestamp object
      if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      }
      
      // Handle regular date string or Date object
      const date = new Date(timestamp)
      return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
      })
    },
    
    getDepartmentName(departmentId) {
      const dept = this.departments.find(d => d.id === departmentId)
      return dept ? dept.name : 'Unknown Department'
    },    
    getLocationName(locationId) {
      const loc = this.locations.find(l => l.id === locationId)
      return loc ? loc.name : 'Unknown Location'
    },

    showCreateDialog() {
      this.createDialog = true
      this.resetNewSession()
    },

    resetNewSession() {
      this.newSession = {
        departmentId: null,
        locationId: null,
        countDate: null,
        description: ''
      }
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation()
      }
    },

    async createSessionCount() {
      if (!this.validForm) return

      this.creatingSession = true
      try {
        const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
        // Get department and location names
        const location = this.locations.find(l => l.id === this.newSession.locationId)

        const response = await this.$store.dispatch('fixedAssets/setFixedAssetsCountByLocation', {
          companyId: companyId,
          departmentId: this.newSession.departmentId,
          departmentName: this.getDepartmentName(this.newSession.departmentId),
          locationId: this.newSession.locationId,
          locationName: location?.name || 'Unknown Location',
          date: this.newSession.countDate,
          description: this.newSession.description,
          forceCount: false
        })
        if (!response || !response.success) {
          throw new Error(response?.error || 'Failed to create session count')
        }
        // Close dialog and reset form
        this.createDialog = false
        // Show success message
        this.snackbar = {
          show: true,
          msg: 'Assets count session created successfully!',
          color: 'success',
          icon: 'mdi-check-circle-outline'
        }

      } catch (error) {
        console.error('Error creating session count:', error)
        this.snackbar = {
          show: true,
          msg: `Error creating session count: ${error}. Please try again.`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        }
      } finally {
        this.creatingSession = false
      }
    },

    showEmailDialog() {
      if (this.selectedList.length === 0) {
        this.snackbar = {
          show: true,
          msg: 'Please select at least one item to send via email',
          color: 'warning',
          icon: 'mdi-alert-circle-outline'
        }
        return
      }
      this.emailDialog = true
      this.resetEmailForm()

      // Auto-populate email addresses based on departmentID and set default checkbox state
      this.selectedList.forEach(item => {
        if (item.departmentID) {
          // Find the matching department
          const department = this.departments.find(dept => dept.id === item.departmentID)
          if (department && department.defaultEmail) {
            // Assign the defaultEmail to this item
            this.$set(this.emailPerItem, item.id, department.defaultEmail)
          }
        }
        // Set default checkbox state to true (include details by default)
        this.$set(this.includeDetailsPerItem, item.id, true)
      })
    },

    resetEmailForm() {
      this.emailPerItem = {}
      this.includeDetailsPerItem = {}
      this.globalMessage = ''
      if (this.$refs.emailForm) {
        this.$refs.emailForm.resetValidation()
      }
    },

    isAllEmailsValid() {
      // Check if all selected items have a valid email
      const emailRegex = /.+@.+\..+/
      return this.selectedList.every(item => {
        const email = this.emailPerItem[item.id]
        return email && email.trim() !== '' && emailRegex.test(email)
      })
    },

    async sendEmails() {
      if (!this.isAllEmailsValid()) return

      this.sendingEmail = true
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId')

      let successCount = 0
      let failCount = 0
      const errors = []

      try {
        // Send email for each item individually
        for (const item of this.selectedList) {
          try {
            const emailPayload = {
              email: this.emailPerItem[item.id],
              message: this.globalMessage,
              includeDetails: this.includeDetailsPerItem[item.id] || false,
              selectedItems: [{
                id: item.id,
                locationName: item.locationName,
                countDate: this.formatDate(item.createdAt),
                assetsCount: item.assetsCount,
                assetsTotal: item.assetsTotal,
                progress: (item.assetsCount / item.assetsTotal) * 100,
                status: item.status,
                countedBy: item.countedBy
              }],
              companyId: companyId
            }
            //console.log('this email payload', emailPayload  )
            

            const response = await this.$store.dispatch('fixedAssets/sendAssetsCountEmail', emailPayload)

            if (response && response.success) {
              successCount++
            } else {
              failCount++
              errors.push(`${item.locationName}: ${response?.error || 'Unknown error'}`)
            }
          } catch (error) {
            failCount++
            errors.push(`${item.locationName}: ${error.message || error}`)
          }
        }

        // Close dialog
        this.emailDialog = false

        // Show result
        if (failCount === 0) {
          this.snackbar = {
            show: true,
            msg: `All ${successCount} email(s) sent successfully!`,
            color: 'success',
            icon: 'mdi-check-circle-outline'
          }
        } else if (successCount === 0) {
          this.snackbar = {
            show: true,
            msg: `Failed to send all emails. ${errors.join('; ')}`,
            color: 'error',
            icon: 'mdi-alert-circle-outline'
          }
        } else {
          this.snackbar = {
            show: true,
            msg: `Sent ${successCount} email(s), ${failCount} failed. Errors: ${errors.join('; ')}`,
            color: 'warning',
            icon: 'mdi-alert-outline'
          }
        }

        // Clear selection if all succeeded
        if (failCount === 0) {
          this.selectedList = []
        }

      } catch (error) {
        console.error('Error sending emails:', error)
        this.snackbar = {
          show: true,
          msg: `Error sending emails: ${error.message || error}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        }
      } finally {
        this.sendingEmail = false
      }
    },

    navigateToAssetsListPage(item) {
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
      this.$router.push({
        name: 'fixedAssetsCountDetail',
        params: {
          companyId: companyId,
          countId: item.id
        }
      })
    },
    getCountStatusColor(status) {
      if (!status || status === 'Uncounted' || status === 'uncounted') return 'grey'
      if (status === 'Inprogress' || status === 'inprogress' || status === 'InProgress' || status === 'in progress') return 'warning'
      if (status === 'Complete' || status === 'complete' || status === 'Completed' || status === 'completed') return 'success'
      return 'grey'
    },

    showCloseDialog() {
      if (this.selectedList.length === 0) {
        this.snackbar = {
          show: true,
          msg: 'Please select at least one item to close',
          color: 'warning',
          icon: 'mdi-alert-circle-outline'
        }
        return
      }

      // Check if all selected items are already complete
      if (this.selectedIncompleteItems.length === 0) {
        this.snackbar = {
          show: true,
          msg: 'All selected items are already closed/complete. Please select items that are in progress.',
          color: 'warning',
          icon: 'mdi-alert-circle-outline'
        }
        return
      }

      // Show warning if some items are already complete
      if (this.selectedCompleteItems.length > 0) {
        this.snackbar = {
          show: true,
          msg: `${this.selectedCompleteItems.length} of ${this.selectedList.length} selected items are already complete and will be skipped.`,
          color: 'info',
          icon: 'mdi-information-outline'
        }
      }

      this.closeDialog = true
    },

    async confirmCloseCount() {
      if (this.selectedIncompleteItems.length === 0) return

      this.closingCount = true
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId')

      let successCount = 0
      let failCount = 0
      const errors = []
      const skippedCount = this.selectedCompleteItems.length

      try {
        // Close only incomplete count sessions
        for (const item of this.selectedIncompleteItems) {
          try {
            const response = await this.$store.dispatch('fixedAssets/closeAssetsCount', {
              companyId: companyId,
              countId: item.id
            })

            if (response && response.success) {
              successCount++
            } else {
              failCount++
              errors.push(`${item.locationName}: ${response?.error || 'Unknown error'}`)
            }
          } catch (error) {
            failCount++
            errors.push(`${item.locationName}: ${error.message || error}`)
          }
        }

        // Close dialog
        this.closeDialog = false

        // Build result message
        let message = ''
        if (failCount === 0) {
          message = `Successfully closed ${successCount} assets count session(s)!`
          if (skippedCount > 0) {
            message += ` (${skippedCount} already complete - skipped)`
          }
          this.snackbar = {
            show: true,
            msg: message,
            color: 'success',
            icon: 'mdi-check-circle-outline'
          }
        } else if (successCount === 0) {
          message = `Failed to close all sessions. ${errors.join('; ')}`
          this.snackbar = {
            show: true,
            msg: message,
            color: 'error',
            icon: 'mdi-alert-circle-outline'
          }
        } else {
          message = `Closed ${successCount} session(s), ${failCount} failed. Errors: ${errors.join('; ')}`
          if (skippedCount > 0) {
            message += ` (${skippedCount} skipped)`
          }
          this.snackbar = {
            show: true,
            msg: message,
            color: 'warning',
            icon: 'mdi-alert-outline'
          }
        }

        // Clear selection if all succeeded
        if (failCount === 0) {
          this.selectedList = []
        }

        // Refresh the data
        await this.$store.dispatch('fixedAssets/fetchAssetCount', { 
          companyId,
          userEmail: this.user?.email || null,
          isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
        })

      } catch (error) {
        console.error('Error closing assets count:', error)
        this.snackbar = {
          show: true,
          msg: `Error closing assets count: ${error.message || error}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        }
      } finally {
        this.closingCount = false
      }
    },

    async fetchCompanyUsers() {
      const companyId = this.$route.params.companyId || localStorage.getItem('companyId')

      try {
        const fc = require('@/firebaseConfig').default
        const snapshot = await fc.db
          .collection("CompaniesUsers")
          .where("companyID", "==", companyId)
          .get()

        // Extract unique email addresses for users with 'owner' or 'user' role
        const emailsSet = new Set()
        snapshot.forEach((doc) => {
          const data = doc.data()
          const userRole = getRole(data)
          // Only include users with 'owner' or 'user' role
          if (data.user && data.user.email && (userRole === 'owner' || userRole === 'user')) {
            emailsSet.add(data.user.email)
          }
        })

        this.companyUsers = Array.from(emailsSet).sort()
      } catch (error) {
        console.error('Error fetching company users:', error)
        this.snackbar = {
          show: true,
          msg: `Error loading company users: ${error.message}`,
          color: 'error',
          icon: 'mdi-alert-circle-outline'
        }
      }
    },
    handleStorageChange(event) {
      // Handle localStorage changes from other tabs/windows
      if (event.key === 'pageLimit' && event.newValue !== event.oldValue) {
        const companyId = this.$route.params.companyId || localStorage.getItem('companyId');
        if (companyId) {
          this.$store.dispatch('fixedAssets/syncItemsPerPageFromLocalStorage', {
            companyId,
            userEmail: this.user?.email || null,
            isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
          });
        }
      }
    }
  },
    mounted() {
    const companyId = this.$route.params.companyId || localStorage.getItem('companyId')
    const pageLimit = localStorage.getItem("pageLimit")
    const initialItemsPerPage = pageLimit === "auto" ? 10 : parseInt(pageLimit) || 10
    
    if (companyId) {
      // Wait for user data to be available
      this.$nextTick(() => {
        // Sync itemsPerPage from localStorage first
        this.$store.dispatch('fixedAssets/syncItemsPerPageFromLocalStorage', {
          companyId,
          userEmail: this.user?.email || null,
          isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
        });
        
        this.fetchDepartments({ companyId })
        this.fetchLocations({ companyId })
        this.fetchAssetsCount({ 
          companyId, 
          itemsPerPage: initialItemsPerPage,
          userEmail: this.user?.email || null,
          isUserRole: this.role.user && !this.role.owner && !this.isDevOrEngineer
        })
        if(this.role.owner || this.role.developer || this.role.engineer) {
          this.fetchCompanyUsers()
        }
      })
    }

    // Listen for localStorage changes (for when pageLimit is changed in another tab/window)
    window.addEventListener('storage', this.handleStorageChange);
  },
  beforeDestroy() {
    // Remove localStorage event listener
    window.removeEventListener('storage', this.handleStorageChange);
  }
}
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 300px;
}

.order-by-row {
  cursor: pointer;
}

.order-by-row:hover {
  background-color: #f5f5f5;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: #f5f5f5;
}

.clickable-row td {
  transition: background-color 0.2s;
}

.v-chip {
  font-weight: bold;
}

.v-progress-linear {
  border-radius: 10px;
}
</style>